// Fast free translation service using MyMemory API
class FastTranslationService {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 1000;
    this.apiEndpoints = [
      'https://api.mymemory.translated.net/get', // Primary: MyMemory API
      'https://libretranslate.de/translate' // Fallback: LibreTranslate
    ];
  }

  // Language code mapping
  getLanguageCode(language) {
    const languageMap = {
      'en': 'en',
      'es': 'es', 
      'fr': 'fr',
      'de': 'de',
      'it': 'it',
      'pt': 'pt',
      'ro': 'ro',
      'cs': 'cs',
    };
    return languageMap[language] || language;
  }

  // Create cache key
  getCacheKey(text, targetLang, sourceLang = 'auto') {
    return `${text}_${sourceLang}_${targetLang}`;
  }

  // Add to cache with size limit
  addToCache(key, value) {
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  // Translate using MyMemory API (fast and free)
  async translateWithMyMemory(text, targetLang, sourceLang = 'auto') {
    try {
      const url = new URL('https://api.mymemory.translated.net/get');
      url.searchParams.append('q', text);
      url.searchParams.append('langpair', `${sourceLang}|${targetLang}`);
      
      console.log('MyMemory API URL:', url.toString());
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      console.log('MyMemory response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('MyMemory response data:', data);
      
      if (data.responseStatus === 200 && data.responseData) {
        return data.responseData.translatedText;
      }
      
      throw new Error('MyMemory translation failed: ' + JSON.stringify(data));
    } catch (error) {
      console.error('MyMemory API error:', error);
      throw error;
    }
  }

  // Translate using LibreTranslate (fallback)
  async translateWithLibreTranslate(text, targetLang, sourceLang = 'auto') {
    try {
      console.log('Trying LibreTranslate API...');
      
      const response = await fetch('https://libretranslate.de/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: text,
          source: sourceLang,
          target: targetLang,
          format: 'text'
        })
      });
      
      console.log('LibreTranslate response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('LibreTranslate response data:', data);
      
      if (data.translatedText) {
        return data.translatedText;
      }
      
      throw new Error('LibreTranslate translation failed: ' + JSON.stringify(data));
    } catch (error) {
      console.error('LibreTranslate API error:', error);
      throw error;
    }
  }

  // Simple fallback translation using basic word mapping
  getFallbackTranslation(text, targetLang) {
    const translations = {
      'es': {
        'hello': 'hola',
        'world': 'mundo',
        'sale': 'venta',
        'discount': 'descuento',
        'promotion': 'promoción',
        'new': 'nuevo',
        'special': 'especial',
        'offer': 'oferta',
        'free': 'gratis',
        'limited': 'limitado',
        'una': 'una',
        'nuova': 'nueva',
        'ondata': 'ola',
        'di': 'de',
        'freschezza': 'frescura',
        'glaciale': 'glacial',
        'quando': 'cuando',
        'utilizzato': 'utilizado',
        'con': 'con',
        'stick': 'palitos',
        'compatibili': 'compatibles',
        'dispositivi': 'dispositivos'
      },
      'fr': {
        'hello': 'bonjour',
        'world': 'monde',
        'sale': 'vente',
        'discount': 'réduction',
        'promotion': 'promotion',
        'new': 'nouveau',
        'special': 'spécial',
        'offer': 'offre',
        'free': 'gratuit',
        'limited': 'limité',
        'una': 'une',
        'nuova': 'nouvelle',
        'ondata': 'vague',
        'di': 'de',
        'freschezza': 'fraîcheur',
        'glaciale': 'glaciale',
        'quando': 'quand',
        'utilizzato': 'utilisé',
        'con': 'avec',
        'stick': 'bâtonnets',
        'compatibili': 'compatibles',
        'dispositivi': 'appareils'
      },
      'de': {
        'hello': 'hallo',
        'world': 'welt',
        'sale': 'verkauf',
        'discount': 'rabatt',
        'promotion': 'aktion',
        'new': 'neu',
        'special': 'speziell',
        'offer': 'angebot',
        'free': 'kostenlos',
        'limited': 'begrenzt',
        'una': 'eine',
        'nuova': 'neue',
        'ondata': 'welle',
        'di': 'von',
        'freschezza': 'frische',
        'glaciale': 'eisig',
        'quando': 'wenn',
        'utilizzato': 'verwendet',
        'con': 'mit',
        'stick': 'sticks',
        'compatibili': 'kompatibel',
        'dispositivi': 'geräte'
      }
    };

    const langTranslations = translations[targetLang] || {};
    let translatedText = text.toLowerCase();
    
    Object.keys(langTranslations).forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      translatedText = translatedText.replace(regex, langTranslations[word]);
    });
    
    return translatedText;
  }

  // Main translation function with fallbacks
  async translateText(text, targetLanguage, sourceLanguage = 'auto') {
    if (!text || text.trim().length === 0) {
      return text;
    }

    const targetLang = this.getLanguageCode(targetLanguage);
    const sourceLang = sourceLanguage === 'auto' ? 'auto' : this.getLanguageCode(sourceLanguage);
    const cacheKey = this.getCacheKey(text, targetLang, sourceLang);

    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // If source and target are the same, return original
    if (sourceLang === targetLang) {
      this.addToCache(cacheKey, text);
      return text;
    }

    try {
      console.log(`Translating: "${text}" from ${sourceLang} to ${targetLang}`);
      
      let translatedText;
      
      try {
        // Try MyMemory API first (faster)
        translatedText = await this.translateWithMyMemory(text, targetLang, sourceLang);
      } catch (error) {
        console.warn('MyMemory failed, trying LibreTranslate:', error);
        try {
          // Fallback to LibreTranslate
          translatedText = await this.translateWithLibreTranslate(text, targetLang, sourceLang);
        } catch (libreError) {
          console.warn('LibreTranslate failed, using fallback translation:', libreError);
          // Final fallback to basic word mapping
          translatedText = this.getFallbackTranslation(text, targetLang);
        }
      }
      
      // Cache the result
      this.addToCache(cacheKey, translatedText);
      
      console.log(`Translation result: "${translatedText}"`);
      return translatedText;
    } catch (error) {
      console.warn('All translation services failed:', error);
      return text; // Return original text on error
    }
  }

  // Detect language (simplified - just return 'auto' for now)
  async detectLanguage(text) {
    if (!text || text.trim().length === 0) {
      return 'en';
    }
    return 'auto'; // Let the translation service detect
  }

  // Batch translate with concurrency limit
  async translateBatch(texts, targetLanguage, sourceLanguage = 'auto') {
    const results = [];
    const batchSize = 3; // Limit concurrent requests
    
    for (let i = 0; i < texts.length; i += batchSize) {
      const batch = texts.slice(i, i + batchSize);
      const batchPromises = batch.map(text => 
        this.translateText(text, targetLanguage, sourceLanguage)
      );
      
      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      } catch (error) {
        console.warn('Batch translation failed:', error);
        results.push(...batch); // Add original texts on error
      }
    }
    
    return results;
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize
    };
  }
}

// Create singleton instance
const fastTranslationService = new FastTranslationService();

export default fastTranslationService;
